module Sm.UI.Login

open MobF

open Sm.Service.App
open Sm.Service.Session

open Sm.UI.Form.Control
open Sm.UI.Form.Basic

type Action =
    | SignIn
    | Register
    | RequestPasswordReset
    | PasswordReset

type Step =
    | Unknown
    | Instruction of string
    | Form of Action * BasicForm

type State = {
    Step: Step
}

type Msg =
    | ShowInstruction of string
    | ShowSignInForm
    | ShowRegisterForm
    | ShowRequestPasswordResetForm
    | ShowPasswordResetForm of string
    | Reset

type Computed =
    abstract Title: string option
    abstract Messages: string list
    abstract Transitions: Button<Msg> list

type LoginModel = Model<State, Msg, Computed>

let create (client: Pocketbase.Client) (session: SessionModel) args =
    let init post =
        match args |> Map.tryFind "prt" with
        | Some token -> post (ShowPasswordResetForm token)
        | None -> post ShowSignInForm

        { Step = Unknown }

    let update (_, post) msg =
        let fullyFilled data =
            data
            |> Map.forall (fun _ v -> v <> "")

        let getField data name =
            data
            |> Map.tryFind name
            |> Option.defaultValue ""

        let handleSubmit invalid f data =
            promise {
                if not (fullyFilled data) then
                    return Error [invalid]
                else
                    let getter = getField data
                    return! f getter
            }

        let handleLoginSubmit invalid f data =
            data |> handleSubmit invalid (fun get -> 
                promise {
                    match! f get with
                    | Ok user -> 
                        session.Post(Login user)
                        return Ok ()
                    | Error messages -> return Error messages
                }
            )

        match msg with
        | ShowInstruction message ->
            { Step = Instruction message }

        | ShowSignInForm ->
            let options = {
                SubmitLabel = "Sign In"
                FieldDefinitions = [
                    Field.email "Email" "email"
                    Field.password "Password" "password"
                ]
                HandleSubmission = handleLoginSubmit "An email and password is required to sign in" (fun get ->
                    let email = get "email"
                    let password = get "password"
                    client |> User.authenticate email password
                )
            }
            let form = Form (SignIn, create options)
            { Step = form }

        | ShowRegisterForm ->
            let options = {
                SubmitLabel = "Register"
                FieldDefinitions = [
                    Field.text "Name" "name"
                    Field.email "Email" "email"
                    Field.password "Password" "password"
                    Field.password "Confirm Password" "passwordConfirm"
                ]
                HandleSubmission = handleLoginSubmit "A name, email and password are required to register" (fun get ->
                    let registration = {
                        Name = get "name"
                        Email = get "email"
                        Password = get "password"
                        PasswordConfirm = get "passwordConfirm"
                    }
                    client |> User.register registration
                )
            }
            let form = Form (Register, create options)
            { Step = form }

        | ShowRequestPasswordResetForm ->
            let options = {
                SubmitLabel = "Send reset instructions"
                FieldDefinitions = [
                    Field.email "Email" "email"
                ]
                HandleSubmission = handleSubmit "An email address is required to reset your password" (fun get ->
                    promise {
                        let email = get "email"

                        match! client |> User.requestPasswordReset email with
                        | Error messages -> return Error messages
                        | Ok () -> 
                            post (ShowInstruction "Check your email for instructions on resetting your password")
                            return Ok ()
                    }
                )
            }
            let form = Form (RequestPasswordReset, create options)
            { Step = form }

        | ShowPasswordResetForm token ->
            let options = {
                SubmitLabel = "Reset Password"
                FieldDefinitions = [
                    Field.password "New Password" "password"
                    Field.password "Confirm New Password" "passwordConfirm"
                ]
                HandleSubmission = handleSubmit "A new password is required" (fun get ->
                    promise {
                        let password = get "password"
                        let passwordConfirm = get "passwordConfirm"

                        match! client |> User.confirmPasswordReset token password passwordConfirm with
                        | Error messages -> return Error messages
                        | Ok () -> 
                            post (ShowInstruction "Your password has been successfully reset, use the link below to return to sign in")
                            return Ok ()
                    }
                )
            }
            let form = Form (PasswordReset, create options)
            { Step = form }

        | Reset ->
            session.Post(Reboot)
            { Step = Unknown }

    let compute state =
        { new Computed with
            member _.Title =
                match state.Step with
                | Form (Register, _) -> Some "Register an account"
                | Form (RequestPasswordReset, _) -> Some "Reset your password"
                | Form (PasswordReset, _) -> Some "Enter a new password"
                | _ -> None

            member _.Messages =
                match state.Step with
                | Form (_, form) -> form.Computed.Messages
                | _ -> []

            member _.Transitions =
                let s = { ButtonState.Default with Subtle = true }
                [
                    match state.Step with
                    | Form (SignIn, _) ->
                        { Content = Label "Register an account"; Command = ShowRegisterForm; State = s }
                        { Content = Label "Forgotten password?"; Command = ShowRequestPasswordResetForm; State = s }
                    | Form (Register, _) ->
                        { Content = Label "Use existing account"; Command = ShowSignInForm; State = s }
                    | _ ->
                        { Content = Label "Return to sign in"; Command = Reset; State = s }
                ]
        }

    Model.useInitWithPost init
    |> Model.andUpdateWithPost update
    |> Model.createWithComputed compute
